import React, {useEffect, useRef, useState} from 'react';
import RewardSetup from '@/containers/Rewards/RewardSetup/RewardSetup';
import RewardDisplay from '@/containers/Rewards/RewardDisplay/RewardDisplay';
import RewardAttributes from '@/containers/Rewards/RewardAttributes/RewardAttributes';
import RewardReview from '@/containers/Rewards/RewardReview/RewardReview';
import {RewardFlowWrapper} from '@/containers/Rewards/common/components';
import {useRewardsEdit} from '@/containers/Rewards/common/hooks';
import {
  DEFAULT_VALUES,
  SECTION_TITLES
} from '@/containers/Rewards/RewardReview/components/common/ReviewConstants';
import {
  DISABLE_EDIT_REWARD_STATUS,
  DISABLE_PUBLISH_REWARD_STATUS
} from '@/utils/constants/rewardsConstants';

const {REWARD_REVIEW} = SECTION_TITLES.DISPLAY;

/**
 * EditReward component - Edit flow only
 * Handles editing existing rewards with conditional navigation
 */
const EditReward = () => {
  const [isCancel, setIsCancel] = useState(false);
  const [showProgressOnReview, setShowProgressOnReview] = useState(false);
  const prevStepRef = useRef(null);

  const {
    formData,
    currentStep,
    setStep,
    handleNext,
    handleDraft,
    handleCancel,
    hasFormErrors,
    disableDraft,
    isLoading
  } = useRewardsEdit();

  useEffect(() => {
    const prevStep = prevStepRef.current;
    if (prevStep == 3 && currentStep == 4) {
      setShowProgressOnReview(true);
    }

    if (currentStep !== 4) {
      setShowProgressOnReview(false);
    }

    prevStepRef.current = currentStep;
  }, [currentStep]);

  const handleCancelClick = () => {
    setIsCancel(true);
  };

  const handleCancelConfirm = () => {
    handleCancel();
    setIsCancel(false);
  };

  const handleCancelReject = () => {
    setIsCancel(false);
  };

  const handleNextClick = async () => {
    await handleNext();
  };

  const saveDraft = async ({redirectOnSuccess = true} = {}) => {
    await handleDraft({redirectOnSuccess});
  };

  // Render step content based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <RewardSetup onSaveDraft={saveDraft} mode='edit' />;
      case 2:
        return <RewardDisplay />;
      case 3:
        return <RewardAttributes />;
      case 4:
        return <RewardReview mode='edit' />;
      default:
        return <RewardReview mode='edit' />;
    }
  };

  // Get step titles for progress indicator
  const stepTitles = [
    {title: 'Setup'},
    {title: 'Display'},
    {title: 'Attributes'},
    {title: 'Review'}
  ];

  const isReviewStep = currentStep === 4;
  const normalizedStatus = formData?.status?.toUpperCase?.();
  const isEditDisabled =
    !!normalizedStatus && DISABLE_EDIT_REWARD_STATUS.includes(normalizedStatus);
  const isPublishDisabled =
    isReviewStep &&
    !!formData?.status &&
    DISABLE_PUBLISH_REWARD_STATUS.includes(formData.status);
  const footerFormError = isReviewStep
    ? hasFormErrors() || isPublishDisabled
    : hasFormErrors();

  const name = formData?.name || DEFAULT_VALUES.FALLBACK;

  return (
    <RewardFlowWrapper
      mode='edit'
      title={name}
      breadcrumbLabel={REWARD_REVIEW}
      showProgressIndicator={currentStep !== 4 || showProgressOnReview}
      showFooter={true}
      footerProps={{
        disableDraft
      }}
      onCancel={handleCancelClick}
      onNext={handleNextClick}
      onDraft={saveDraft}
      isLoading={isLoading}
      formErrors={footerFormError}
      currentStep={currentStep}
      stepTitles={stepTitles}
      onStepClick={isEditDisabled ? undefined : (step) => setStep(step)}
      isEditDisabled={isEditDisabled}
      showDownload={isReviewStep}
      updatedDate={formData?.updatedAtDisplay}
      isNameValid={true}
      isCancel={isCancel}
      onCancelConfirm={handleCancelConfirm}
      onCancelReject={handleCancelReject}>
      {renderStepContent()}
    </RewardFlowWrapper>
  );
};

export default EditReward;
